// The thin DOM adapter half of the camera-gesture seam (camera-pan-zoom; design.md D1) —
// the mostly-untestable-by-construction part, kept as small as possible so the pure math
// (`camera-gestures.ts`) carries the real logic. Attaches:
//
//   - a NON-PASSIVE native `wheel` listener (bypassing React's passive synthetic
//     `onWheel`, whose `preventDefault` is a documented no-op) that ALWAYS calls
//     `preventDefault()` — browser-zoom/scroll suppression is unconditional (plan.md D2),
//     independent of whether `enabled` currently drives the camera;
//   - pointer-capture-based drag-to-pan (mouse/touch/pen unified via Pointer Events),
//     robust to a fast drag whose pointer leaves the element mid-gesture;
//   - two-finger pinch-to-zoom (WAG-93): while two pointers are down the gesture is a zoom
//     about the fingers' midpoint and NOTHING else — no pan, no tap. It reaches `onZoom`
//     through the same coalescing accumulator a wheel does, so a call site that already
//     handles the wheel handles the pinch with no extra wiring. Until this landed the adapter
//     tracked exactly one pointer, so a second finger landing simply became a new drag and a
//     pinch panned the field instead of zooming it;
//   - rAF-coalesced callback delivery (architecture-review F1): a burst of raw wheel/
//     pointermove events between two frames collapses to at most one `onZoom`/`onPan`
//     call per frame;
//   - tap-vs-pan classification (bee-inspect-panel): a press-release whose displacement
//     from the press point never leaves `TAP_SLOP_PX` is a TAP (`onTap`); anything beyond
//     it latches as a drag for the rest of the gesture. Pan deltas are WITHHELD until that
//     latch trips, so a tap's own finger-jitter never nudges the camera — the alternative
//     (pan everything, then also tap) makes every tap a 1-8px camera shove. Tap lives here
//     rather than in a second `pointerup` listener on the same element precisely so the two
//     interpretations of one pointer sequence cannot race.
//
// `enabled` gates whether `onZoom`/`onPan` are actually invoked; the listeners, the
// `preventDefault`, and pointer capture itself are NOT gated on it (suppression is
// unconditional). `raf` is an injectable scheduler (mirrors the `Scheduler`/`Rng`
// injectable-seam convention already used by `SessionController`) so tests can control
// frame timing deterministically; production defaults to `window.requestAnimationFrame`.

import { useEffect, type RefObject } from 'react';
import { pinchAnchor, pinchSeparation, pinchZoomFactor, wheelZoomFactor } from './camera-gestures';

export interface CanvasCameraGestureHandlers {
  /** Called at most once per animation frame with the screen point of the LAST wheel
   * event in the coalesced burst and the ACCUMULATED (multiplied) zoom factor since the
   * last flush. */
  onZoom(screenPoint: { x: number; y: number }, factor: number): void;
  /** Called at most once per animation frame with the ACCUMULATED (summed) pan delta,
   * in screen pixels, since the last flush. */
  onPan(dxPx: number, dyPx: number): void;
  /** Called once, synchronously on `pointerup`, when the gesture never left
   * {@link TAP_SLOP_PX} of its press point — with the release point in CLIENT screen
   * coordinates (the same frame `onZoom` reports its point in, so a call site converts both
   * to container-local coordinates the same way). Optional: a call site that only wants
   * pan/zoom omits it and nothing changes. NOT rAF-coalesced — a tap is one discrete event
   * with nothing to coalesce, and delaying it a frame only adds latency to a touch. */
  onTap?(screenPoint: { x: number; y: number }): void;
}

/** How far (screen px, Euclidean, from the press point) a pointer may travel and still be
 * a tap. 8 px is the platform-conventional touch slop (Android's `ViewConfiguration`
 * default; browsers use ~5 px for synthetic clicks) — large enough that a real finger press
 * on a phone survives, far below the 22 px pick radius so it cannot walk a tap onto a
 * neighbouring bee. */
export const TAP_SLOP_PX = 8;

/** A press that starts on floating HUD chrome over the canvas (the camera rail, the bee
 * inspect panel) is that chrome's press, not a canvas tap — so it must not also select
 * whatever the chrome happens to be covering. Chrome opts out either by being a `<button>`
 * or by carrying `data-gesture-chrome` (which covers a panel's non-button body too). Pan
 * and zoom are deliberately NOT gated on this: dragging off a button has always panned, and
 * changing that is not this change's business. */
function isChromePress(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('button, [data-gesture-chrome]') !== null;
}

export interface RafScheduler {
  requestFrame(cb: FrameRequestCallback): number;
  cancelFrame(id: number): void;
}

// A SINGLETON, not a per-call factory: the hook's `raf` parameter defaults to this one
// constant object, so an uncontrolled call site (no explicit `raf` argument) gets the SAME
// reference on every render. A `windowRafScheduler(): RafScheduler { return {...} }`
// factory invoked as the default-parameter EXPRESSION would instead construct a fresh
// object every call — since a JS default parameter re-evaluates its expression on every
// invocation — which would change the hook's effect-dependency identity on every render
// and tear down + reattach the listeners, losing in-flight gesture state (an active drag's
// `dragPointerId`, a pending coalesced accumulator) exactly when a driven camera update
// re-renders the caller. Caught by `Match.test.tsx`'s drag-to-pan test during TDD.
const DEFAULT_RAF_SCHEDULER: RafScheduler = {
  requestFrame: (cb) => window.requestAnimationFrame(cb),
  cancelFrame: (id) => window.cancelAnimationFrame(id),
};

/** Calls `el.setPointerCapture`/`releasePointerCapture` only if present — real browsers
 * always have them; jsdom (this codebase's test environment) does not implement pointer
 * capture at all, so this guard is not merely defensive-for-production, it is what keeps
 * a test double (a plain object without the method) from crashing the adapter. */
function hasPointerCapture(
  el: unknown,
): el is { setPointerCapture(id: number): void; releasePointerCapture(id: number): void } {
  return (
    typeof (el as { setPointerCapture?: unknown }).setPointerCapture === 'function' &&
    typeof (el as { releasePointerCapture?: unknown }).releasePointerCapture === 'function'
  );
}

export function useCanvasCameraGestures(
  containerRef: RefObject<HTMLElement | null>,
  handlers: CanvasCameraGestureHandlers,
  enabled: boolean,
  raf: RafScheduler = DEFAULT_RAF_SCHEDULER,
): void {
  useEffect(() => {
    const el = containerRef.current;
    if (el === null) {
      return;
    }

    // Pending, coalesced deltas — flushed by at most one scheduled rAF at a time.
    let pendingZoomFactor = 1;
    let pendingZoomPoint: { x: number; y: number } | null = null;
    let pendingPanDx = 0;
    let pendingPanDy = 0;
    let frameId: number | null = null;
    let dragPointerId: number | null = null;
    let lastDragPoint: { x: number; y: number } | null = null;
    // Tap-vs-pan state: where the press started, whether it has latched into a drag, and
    // whether it started on chrome (in which case it can never become a tap).
    let pressPoint: { x: number; y: number } | null = null;
    let isDrag = false;
    let pressOnChrome = false;
    // Every pointer currently down, in the order it landed — a pinch is always the FIRST TWO,
    // so a third finger joining mid-gesture does not re-anchor a pinch already in progress.
    // Positions are mutated in place on `pointermove`; the map is the single source of truth for
    // "how many fingers are on the glass", which is what decides pinch vs drag.
    const activePointers = new Map<number, { x: number; y: number }>();
    /** The finger separation the next pinch move measures its ratio against — non-null exactly
     * while a pinch is live. */
    let pinchBaseSeparation: number | null = null;

    function firstTwoPointers(): [{ x: number; y: number }, { x: number; y: number }] | null {
      const [a, b] = [...activePointers.values()];
      return a === undefined || b === undefined ? null : [a, b];
    }

    /** A second finger has landed: the gesture becomes a pinch, and whatever it was doing as a
     * one-finger gesture stops. A LATCHED pan delta is left pending (it was real movement, and
     * the flush that honours it is owed); unlatched slop is discarded, exactly as a tap's is. */
    function beginPinch(): void {
      const pair = firstTwoPointers();
      if (pair === null) {
        return;
      }
      if (!isDrag) {
        pendingPanDx = 0;
        pendingPanDy = 0;
      }
      dragPointerId = null;
      lastDragPoint = null;
      pressPoint = null;
      pressOnChrome = false;
      pinchBaseSeparation = pinchSeparation(pair[0], pair[1]);
    }

    function scheduleFlush(): void {
      if (frameId !== null) {
        return;
      }
      frameId = raf.requestFrame(() => {
        frameId = null;
        if (pendingZoomPoint !== null && pendingZoomFactor !== 1) {
          if (enabled) {
            handlers.onZoom(pendingZoomPoint, pendingZoomFactor);
          }
          pendingZoomFactor = 1;
          pendingZoomPoint = null;
        }
        // Withheld until the gesture latches as a drag — an unlatched accumulator is kept
        // (not dropped), so the frame that trips the latch delivers the WHOLE delta from the
        // press point and the camera never lags by the slop.
        if (isDrag && (pendingPanDx !== 0 || pendingPanDy !== 0)) {
          if (enabled) {
            handlers.onPan(pendingPanDx, pendingPanDy);
          }
          pendingPanDx = 0;
          pendingPanDy = 0;
        }
      });
    }

    function onWheel(e: WheelEvent): void {
      // Suppression is unconditional (plan.md D2) — regardless of `enabled`.
      e.preventDefault();
      // `deltaMode` is forwarded, not dropped: it is the UNIT `deltaY` is expressed in, and
      // reading the magnitude without it gave a line-mode mouse a thirtieth of a trackpad's
      // zoom step (see `wheelZoomFactor`). Fields are named explicitly rather than spreading
      // the event, so this stays the only place that touches the DOM shape.
      pendingZoomFactor *= wheelZoomFactor({
        deltaY: e.deltaY,
        deltaMode: e.deltaMode,
        ctrlKey: e.ctrlKey,
      });
      pendingZoomPoint = { x: e.clientX, y: e.clientY };
      scheduleFlush();
    }

    /** WebKit's own non-standard pinch gesture (`gesturestart`/`gesturechange`), suppressed for
     * the same reason and on the same unconditional terms as `wheel`: on iOS, page pinch-zoom is a
     * browser-level gesture that `touch-action: none` does not reliably claim, and a pinch that
     * zooms the whole page instead of the field is exactly the bug WAG-93 is about. No other engine
     * fires these, so on every other browser this listener never runs. */
    function onWebkitGesture(e: Event): void {
      e.preventDefault();
    }

    function onPointerDown(e: PointerEvent): void {
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (hasPointerCapture(e.target)) {
        e.target.setPointerCapture(e.pointerId);
      }
      if (activePointers.size >= 2) {
        beginPinch();
        return;
      }
      dragPointerId = e.pointerId;
      lastDragPoint = { x: e.clientX, y: e.clientY };
      pressPoint = { x: e.clientX, y: e.clientY };
      isDrag = false;
      pressOnChrome = isChromePress(e.target);
    }

    function onPointerMove(e: PointerEvent): void {
      const tracked = activePointers.get(e.pointerId);
      if (tracked !== undefined) {
        tracked.x = e.clientX;
        tracked.y = e.clientY;
      }
      if (pinchBaseSeparation !== null) {
        const pair = firstTwoPointers();
        if (pair === null) {
          return;
        }
        const separation = pinchSeparation(pair[0], pair[1]);
        pendingZoomFactor *= pinchZoomFactor(pinchBaseSeparation, separation);
        // The baseline advances even when the ratio was refused as degenerate: a pinch that
        // collapses its fingers together and spreads them again resumes from where they are,
        // rather than measuring the whole spread against a two-pixel divisor.
        pinchBaseSeparation = separation;
        pendingZoomPoint = pinchAnchor(pair[0], pair[1]);
        scheduleFlush();
        return;
      }
      if (dragPointerId === null || e.pointerId !== dragPointerId || lastDragPoint === null) {
        return;
      }
      pendingPanDx += e.clientX - lastDragPoint.x;
      pendingPanDy += e.clientY - lastDragPoint.y;
      lastDragPoint = { x: e.clientX, y: e.clientY };
      // The latch is one-way for the rest of the gesture: a drag that wanders out past the
      // slop and back to its origin is still a drag, never a (surprise) tap on release.
      if (
        !isDrag &&
        pressPoint !== null &&
        Math.hypot(e.clientX - pressPoint.x, e.clientY - pressPoint.y) > TAP_SLOP_PX
      ) {
        isDrag = true;
      }
      scheduleFlush();
    }

    /** One finger has left a pinch. Two or more remain: the pinch continues, re-baselined on
     * whichever two are now first. Exactly one remains: the gesture continues as a pan
     * re-anchored to the survivor's OWN position — re-anchoring is what stops the world lurching
     * by the gap between the two fingers — and it is already latched as a drag, so its eventual
     * release can never be read as a tap on whatever it happens to be resting over. None remain:
     * the gesture is over, and any withheld pan dies with it. */
    function endPinch(): void {
      const pair = firstTwoPointers();
      if (pair !== null) {
        pinchBaseSeparation = pinchSeparation(pair[0], pair[1]);
        return;
      }
      pinchBaseSeparation = null;
      const [survivorId] = [...activePointers.keys()];
      const survivor = survivorId === undefined ? undefined : activePointers.get(survivorId);
      if (survivorId !== undefined && survivor !== undefined) {
        dragPointerId = survivorId;
        lastDragPoint = { x: survivor.x, y: survivor.y };
        pressPoint = { x: survivor.x, y: survivor.y };
        isDrag = true;
        pressOnChrome = false;
        return;
      }
      dragPointerId = null;
      lastDragPoint = null;
      pressPoint = null;
      pressOnChrome = false;
      pendingPanDx = 0;
      pendingPanDy = 0;
    }

    /** The single end-of-pointer path for both `pointerup` and `pointercancel`: forget the
     * pointer, release its capture, then let whichever gesture it belonged to finish. Capture is
     * released HERE rather than inside {@link endDrag} because a pinch's pointers are captured
     * too and no `dragPointerId` names them. */
    function endPointer(e: PointerEvent, tapped: boolean): void {
      const wasTracked = activePointers.delete(e.pointerId);
      if (wasTracked && hasPointerCapture(e.target)) {
        e.target.releasePointerCapture(e.pointerId);
      }
      if (pinchBaseSeparation !== null) {
        endPinch();
        return;
      }
      endDrag(e, tapped);
    }

    function endDrag(e: PointerEvent, tapped: boolean): void {
      if (dragPointerId === null || e.pointerId !== dragPointerId) {
        return;
      }
      // The gesture's final leg may have arrived only as THIS `pointerup` — a fast
      // press-and-release whose last movement never fired its own `pointermove` (coalesced
      // away, or simply never dispatched). Fold in whatever delta accrued since the last known
      // drag point, and classify against the RELEASE position, not just whatever `pointermove`s
      // already latched `isDrag` — otherwise a real pan beyond the slop is misread as a tap
      // (bee-inspect-panel review finding).
      if (lastDragPoint !== null) {
        pendingPanDx += e.clientX - lastDragPoint.x;
        pendingPanDy += e.clientY - lastDragPoint.y;
        lastDragPoint = { x: e.clientX, y: e.clientY };
      }
      if (
        !isDrag &&
        pressPoint !== null &&
        Math.hypot(e.clientX - pressPoint.x, e.clientY - pressPoint.y) > TAP_SLOP_PX
      ) {
        isDrag = true;
      }
      if (isDrag && (pendingPanDx !== 0 || pendingPanDy !== 0)) {
        if (enabled) {
          handlers.onPan(pendingPanDx, pendingPanDy);
        }
        pendingPanDx = 0;
        pendingPanDy = 0;
      }
      if (tapped && !isDrag && !pressOnChrome && enabled) {
        handlers.onTap?.({ x: e.clientX, y: e.clientY });
      }
      if (!isDrag) {
        // Never-latched slop is discarded, not carried into the next gesture.
        pendingPanDx = 0;
        pendingPanDy = 0;
      }
      // `isDrag` deliberately survives the release (the next `pointerdown` resets it): a drag
      // whose final `pointermove` arrived in the same frame as its `pointerup` still has an
      // unflushed delta, and clearing the latch here would make the pending rAF swallow it.
      dragPointerId = null;
      lastDragPoint = null;
      pressPoint = null;
      pressOnChrome = false;
    }

    function onPointerUp(e: PointerEvent): void {
      endPointer(e, true);
    }

    function onPointerCancel(e: PointerEvent): void {
      // A cancelled gesture (the browser took the pointer for a system gesture) is not a
      // deliberate release, so it never taps.
      endPointer(e, false);
    }

    // NOTE: no `pointerleave` listener — that is the old bug this hook fixes. Once
    // `setPointerCapture` succeeds, the Pointer Events spec suppresses boundary events
    // (`pointerleave`/`pointerout`) for the capturing target for the duration of the
    // capture, and `pointerup`/`pointercancel` still reach the capturing element even if
    // released outside it — so those two remain the only reliable end-of-drag signals.
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', onWebkitGesture, { passive: false });
    el.addEventListener('gesturechange', onWebkitGesture, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerCancel);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', onWebkitGesture);
      el.removeEventListener('gesturechange', onWebkitGesture);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerCancel);
      if (frameId !== null) {
        raf.cancelFrame(frameId);
        frameId = null;
      }
    };
    // `containerRef`/`raf` are stable DI seams; `handlers`/`enabled` are read fresh on
    // every event via the closure recreated by this effect re-running when they change.
  }, [containerRef, handlers, enabled, raf]);
}
